import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Film, Mail, Play, Sparkles, Star } from "lucide-react";

import "./../custom-fonts.css";

function MusicStudioPage() {
  const videoRefs = useRef([]);
  const [expanded, setExpanded] = useState([]);

  const user = "contact";
  const domain = "polymaestromusic.com";
  const email = `${user}@${domain}`;
  const heavenfallTrailerUrl = "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/138b5cb5eb09659f80b1333b336ff3bb/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F138b5cb5eb09659f80b1333b336ff3bb%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600";
  

  /*
    POSTER UPLOAD INSTRUCTIONS

    Put your poster image files in:

    public/posters/forsworn-poster.jpg
    public/posters/castles-in-space-poster.jpg
    public/posters/rehearsal-poster.jpg

    If you use different filenames, only update the "poster" fields below.
  */
  const festivalFilms = [
    {
      title: "FORSWORN",
      poster: "/posters/forsworn-poster.png",
      year: "2026",
      runtime: "2:05",
      category: "Narrative Short",
      status: "Festival Film",
      logline:
        "A young queen inherits a kingdom built on war, only to discover that conquest has cost her the man she loves.",
      tagline: "She inherited violence. Then chose to leave it.",
      credits:
        "Written by Amit Karmarkar and Kavita Karmarkar · Original music by Kavita Karmarkar · A Polymaestro Studios Production",
    },
    {
      title: "CASTLES IN SPACE",
      poster: "/posters/castles-in-space-poster.png",
      year: "2026",
      runtime: "5:46",
      category: "Narrative Short",
      status: "Festival Film",
      logline:
        "Following the death of his wife, a grieving astronaut untethers himself into space, surrendering to the void in the hope of finding her again.",
      tagline: "He went into the void to find her. She sent him back.",
      credits:
        "An intimate story of love, loss, and the fragile moment that can change the course of a life.",
    },
    {
      title: "REHEARSAL",
      poster: "/posters/rehearsal-poster.png",
      year: "2026",
      runtime: "1:56",
      category: "Narrative Short",
      status: "Festival Film",
      logline:
        "As a young actress dreams of opening night, a moment from the waking world follows her into the subconscious, where identity itself becomes a performance.",
      tagline: "Identity is not found. It is rehearsed.",
      credits:
        "Written by Amit Karmarkar and Kavita Karmarkar · Music by Kavita Karmarkar · A Polymaestro Studios Production",
    },
    {
      title: "BEFORE IT LANDS",
      poster: "/posters/before-it-lands-poster.png",
      year: "2026",
      runtime: "2:06",
      category: "Narrative Short",
      status: "Festival Film",
      logline:
        "During a ballet performance, two professional dancers begin by portraying love with practiced precision — until something in the music changes, and the emotion they are performing becomes real.",
      tagline: "The dance was rehearsed. The feeling wasn't.",
      credits:
        "Written by Amit Karmarkar · Music by Kavita Karmarkar · A Polymaestro Studios Production",
    },
    {
      title: "THE BEACH REMEMBERS",
      poster: "/posters/the-beach-remembers-poster.png",
      year: "2026",
      runtime: "1:29",
      category: "Narrative Short",
      status: "Festival Film",
      logline:
        "A great-grandson retraces his ancestor's D-Day landing in search of the person history transformed into a hero.",
      tagline: "The family remembers a hero. The beach remembers the boy.",
      credits:
        "Written by Amit Karmarkar · Music by Kavita Karmarkar · A Polymaestro Studios Production",
    },
    {
      title: "THE GOOD LAST DAYS",
      poster: "/posters/the-good-last-days-poster.png",
      year: "2026",
      runtime: "1:25",
      category: "Narrative Short",
      status: "Festival Film",
      logline:
        "With her beloved Labrador nearing the end of his life, a young woman gives him one last taste of freedom — and is left to imagine the final days she hopes he had.",
      tagline: "She gave him freedom. She lost the ending.",
      credits:
        "Written by Amit Karmarkar · Music by Kavita Karmarkar · A Polymaestro Studios Production",
    }
  ];

  // Original randomization logic preserved.
  const videos = useMemo(() => {
    const randomizePair = Math.random() < 0.5;

    // Pair 1: Rise of the King & Clockmaster’s Curse
    const riseOfKing = {
      title: "Rise of the King",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/f75366dfc50ec87aae3243bc92a28b0b/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2Ff75366dfc50ec87aae3243bc92a28b0b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "In a kingdom ruled by violence and betrayal, a humble peasant rises to challenge the throne. Guided by courage and a strong sense of justice, he faces powerful enemies, shifting loyalties, and doubts of his own. As war spreads across the kingdom, his fight for what is right grows into a struggle that could change the empire forever. Rise of the King tells the story of an ordinary man who defies the odds to become a ruler.",
      moreVideos: [
        {
          title: "The Clockmaster's Curse",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/19a4e8da10f4a0664df47b3972be50c4/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F19a4e8da10f4a0664df47b3972be50c4%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:23",
          description:
            "When a peaceful king dies, his two daughters fight for the throne, tearing the kingdom apart. As their struggle grows more violent, the mysterious Clockmaster appears from beyond time. He transforms the sisters into mythical creatures and sends them to another age, where their battle for power continues.",
          label: "Fantasy",
        },
        {
          title: "The Making of Darkness",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/9cf239ba6d3c52794a69bd75bcbe2414/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F9cf239ba6d3c52794a69bd75bcbe2414%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:32",
          description:
            "After making a vow of revenge with his brother, a grieving sailor becomes the feared pirate Blackbeard. Driven by anger, he spreads terror across the seas, losing more of himself with every act of violence. But when he finally gets his revenge, he discovers that the man he once was is gone, leaving only the monster he has become.",
          label: "Historical Fiction",
        },
      ],
    };

    const clockmastersCurse = {
      title: "The Clockmaster's Curse",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/19a4e8da10f4a0664df47b3972be50c4/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F19a4e8da10f4a0664df47b3972be50c4%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "When a peaceful king dies, his two daughters fight for the throne, tearing the kingdom apart. As their struggle grows more violent, the mysterious Clockmaster appears from beyond time. He transforms the sisters into mythical creatures and sends them to another age, where their battle for power continues.",
      moreVideos: [
        {
          title: "The Making of Darkness",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/9cf239ba6d3c52794a69bd75bcbe2414/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F9cf239ba6d3c52794a69bd75bcbe2414%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:32",
          description:
            "After making a vow of revenge with his brother, a grieving sailor becomes the feared pirate Blackbeard. Driven by anger, he spreads terror across the seas, losing more of himself with every act of violence. But when he finally gets his revenge, he discovers that the man he once was is gone, leaving only the monster he has become.",
          label: "Historical Fiction",
        },
        {
          title: "Rise of the King",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/f75366dfc50ec87aae3243bc92a28b0b/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2Ff75366dfc50ec87aae3243bc92a28b0b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:23",
          description:
            "In a kingdom ruled by violence and betrayal, a humble peasant rises to challenge the throne. Guided by courage and a strong sense of justice, he faces powerful enemies, shifting loyalties, and doubts of his own. As war spreads across the kingdom, his fight for what is right grows into a struggle that could change the empire forever. Rise of the King tells the story of an ordinary man who defies the odds to become a ruler.",
          label: "Epic Saga",
        },
      ],
    };

    // Pair 2: Assassins Ball, Wicked Game of Love, Dance of Stone
    const assassinsBall = {
      title: "Assassins Ball",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/6b39fcfbaa012464d09b54bdc12fe93b/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F6b39fcfbaa012464d09b54bdc12fe93b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "During a time of peace and prosperity, the wealthy and powerful gather for the Assassins Ball, a grand masquerade filled with music, dancing, and romance. But behind the elegant masks, everyone has secrets. As hidden rivalries and forbidden love begin to surface, a single revelation threatens to turn a beautiful evening into a dangerous game of betrayal.",
      moreVideos: [
        {
          title: "Wicked Game of Love",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/2d8518e9ab1840ed3837135d31cd5bec/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F2d8518e9ab1840ed3837135d31cd5bec%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:39",
          description:
            "Wicked Game of Love tells the story of a romance played like cards on a table—where love is a gamble and no one leaves untouched.",
          label: "Drama",
        },
        {
          title: "Dance of Stone, Fire and Light",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/a86df2820139dcf60954caebcf6159b7/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2Fa86df2820139dcf60954caebcf6159b7%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:41",
          description:
            "Archaeologists uncover a lost civilization whose breathtaking rituals of stone, fire, and light inspire Dance of Stone, Fire and Light, a musical journey into ancient wonder.",
          label: "Musical",
        },
      ],
    };

    const wickedAndDance = {
      title: "Wicked Game of Love",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/2d8518e9ab1840ed3837135d31cd5bec/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F2d8518e9ab1840ed3837135d31cd5bec%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "Wicked Game of Love tells the story of a romance played like cards on a table—where love is a gamble and no one leaves untouched.",
      moreVideos: [
        {
          title: "Dance of Stone, Fire and Light",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/a86df2820139dcf60954caebcf6159b7/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2Fa86df2820139dcf60954caebcf6159b7%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:41",
          description:
            "Archaeologists uncover a lost civilization whose breathtaking rituals of stone, fire, and light inspire Dance of Stone, Fire and Light, a musical journey into ancient wonder.",
          label: "Musical",
        },
        {
          title: "Assassins Ball",
          url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/6b39fcfbaa012464d09b54bdc12fe93b/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F6b39fcfbaa012464d09b54bdc12fe93b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
          runtime: "1:39",
          description:
            "During a time of peace and prosperity, the wealthy and powerful gather for the Assassins Ball, a grand masquerade filled with music, dancing, and romance. But behind the elegant masks, everyone has secrets. As hidden rivalries and forbidden love begin to surface, a single revelation threatens to turn a beautiful evening into a dangerous game of betrayal.",
          label: "Suspense",
        },
      ],
    };

    // Common videos
    const bloodFeud = {
      title: "Blood Feud",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/2c9b61a04a52dfcd3546ebb22e61844b/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F2c9b61a04a52dfcd3546ebb22e61844b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "Bound by blood but divided by ambition, three brothers clash for control of a powerful crime syndicate. What begins as a quiet struggle beneath the surface erupts into a brutal war of strategy, betrayal, and vengeance. As loyalties fracture and bodies fall, each brother must decide how far he’s willing to go to claim the throne. In a world where family is both weapon and weakness, only one can rule.",
    };

    const firstContact = {
      title: "First Contact",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/6c8cb243cb9e94cf8f6fd2abfed3cd86/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F6c8cb243cb9e94cf8f6fd2abfed3cd86%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "An unidentified alien craft begins circling Earth’s orbit, sending shockwaves of panic across the globe. As hysteria mounts and humanity braces for invasion, fear shapes every assumption about the unknown visitors. Yet the truth behind their presence is far more unexpected, and far more human, than anyone imagined.",
    };

    const heartsLeftBehind = {
      title: "Hearts Left Behind",
      file: null,
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/4c7739be696df4bde0b787da7a2c611c/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F4c7739be696df4bde0b787da7a2c611c%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis:
        "As war rages abroad, soldiers and their families are bound by love, distance, and the weight of uncertainty. On the front lines and at home, they face battles both seen and unseen—courage in the field mirrored by resilience in the living room. Letters, memories, and promises become lifelines as hope struggles against the harsh realities of conflict. Hearts Left Behind is a moving portrait of the human cost of war, told through the bonds that endure when everything else is torn apart.",
    };

    const firstPair = randomizePair ? clockmastersCurse : riseOfKing;
    const secondPair = Math.random() < 0.5 ? wickedAndDance : assassinsBall;

    return [firstPair, secondPair];
  }, []);

  useEffect(() => {
    setExpanded(Array(videos.length).fill(false));
  }, [videos]);

  const toggleExpanded = (index) => {
    setExpanded((prev) => prev.map((val, i) => (i === index ? !val : val)));
  };

  const navItems = [
    { label: "Upcoming Film", href: "#superintelligence" },
    { label: "Festival Films", href: "#festival-films" },
    { label: "Studio", href: "#studio" },
    { label: "Catalog", href: "#catalogue" },
    { label: "Contact", href: "#contact" },
  ];

  const newsItems = [
    {
      title:
        "Polymaestro Studios is seeking select collaborations with film producers and filmmakers developing festival-bound projects.",
      date: "July 2026",
      category: "Collaboration Call",
      description:
        "We create original music for cinematic storytelling—music designed not as background, but as the emotional force of the film. We welcome inquiries from filmmakers looking to give their work a more distinctive, memorable, and deeply felt musical identity.",
    },
  ];

  const sceneClips = [
    {
      title: "Unbreakable",
      description:
        "A powerful musical cue capturing the relentless drive and unbreakable spirit of a boxer returning to the ring after tragedy.",
      url: "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/0deaf514b9bf08bc1d3f1b319032eb1f/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F0deaf514b9bf08bc1d3f1b319032eb1f%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
    },
    

  ];

  const communityProjects = [
    {
      title: "Dream Runner",
      cloudflareStreamUrl:
        "https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/1497d285bd07b5dd23e879b97b3cc404/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2F1497d285bd07b5dd23e879b97b3cc404%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      synopsis: (
        <>
          <p>
            <i>
            Hold onto the one thing in life that feels pure and true. For us, it has always been music — the spark that turns imagination into something alive.
            </i>
          </p>
          <p>
          Later this year, we will open the door to new collaborators. We are seeking adventurous artists and creative partners to help bring our next slate of music-driven films to life. Stay tuned.
          </p>
        </>
      ),
    },
  ];

  const sectionLabel = {
    color: "#bfa462",
    fontSize: "0.76rem",
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    fontWeight: 700,
  };

  const sectionTitle = {
    margin: "0",
    fontSize: "clamp(2.7rem, 7vw, 7.5rem)",
    lineHeight: "0.88",
    letterSpacing: "-0.075em",
    fontWeight: 500,
  };

  const missionCopy = {
    color: "#d2ccc2",
    fontSize: "clamp(1.18rem, 1.45vw, 1.4rem)",
    lineHeight: 1.6,
    fontWeight: 400,
  };

  const bodyCopy = {
    color: "#b6b0a6",
    fontSize: "clamp(1.05rem, 1.25vw, 1.25rem)",
    lineHeight: 1.7,
  };

  const shell = {
    width: "min(1440px, calc(100% - 40px))",
    margin: "0 auto",
  };

  const videoFrame = {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    overflow: "hidden",
    backgroundColor: "#000",
  };

  return (
    <main className="pm-page">
      <style>
        {`
          * {
            box-sizing: border-box;
          }

          html {
            scroll-behavior: smooth;
          }

          body {
            margin: 0;
            background: #050505;
          }

          ::selection {
            background: rgba(191, 164, 98, 0.35);
            color: white;
          }

          .pm-page {
            min-height: 100vh;
            color: #f7f2e8;
            font-family: FFDaxProRegular, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            background:
              radial-gradient(circle at 25% 0%, rgba(191, 164, 98, 0.17), transparent 28%),
              radial-gradient(circle at 80% 18%, rgba(108, 30, 30, 0.18), transparent 28%),
              linear-gradient(180deg, #070707 0%, #050505 50%, #090806 100%);
            overflow-x: hidden;
          }

          .pm-page:before {
            content: "";
            position: fixed;
            inset: 0;
            pointer-events: none;
            z-index: 80;
            opacity: 0.045;
            background-image:
              linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px);
            background-size: 64px 64px;
            mask-image: radial-gradient(circle at center, black 0%, transparent 78%);
          }

          .pm-page:after {
            content: "";
            position: fixed;
            inset: -40%;
            pointer-events: none;
            z-index: 90;
            opacity: 0.035;
            background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.82' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.9'/%3E%3C/svg%3E");
            animation: pmGrain .26s steps(2) infinite;
          }

          @keyframes pmGrain {
            0%, 100% { transform: translate(0, 0); }
            25% { transform: translate(-1%, 1%); }
            50% { transform: translate(1%, -1%); }
            75% { transform: translate(1%, 1%); }
          }

          @keyframes pmRise {
            from {
              opacity: 0;
              transform: translateY(26px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .pm-nav-link,
          .pm-mail-link,
          .pm-button {
            transition: color .25s ease, border-color .25s ease, background .25s ease, transform .25s ease;
          }

          .pm-nav-link:hover,
          .pm-mail-link:hover {
            color: #ffffff !important;
          }

          .pm-button:hover {
            transform: translateY(-2px);
            border-color: rgba(191, 164, 98, 0.75) !important;
            background: rgba(191, 164, 98, 0.12) !important;
          }

          .pm-festival-card {
            transition: transform .35s ease, border-color .35s ease, box-shadow .35s ease;
          }

          .pm-festival-card:hover {
            transform: translateY(-10px);
            border-color: rgba(191, 164, 98, .45) !important;
            box-shadow: 0 28px 80px rgba(0, 0, 0, .6);
          }

          .pm-festival-card:hover .pm-poster-img {
            transform: scale(1.035);
          }

          .pm-festival-card:hover .pm-card-line {
            width: 100%;
          }

          .pm-video-card {
            transition: transform .28s ease, border-color .28s ease, background .28s ease;
          }

          .pm-video-card:hover {
            transform: translateY(-5px);
            border-color: rgba(191, 164, 98, .32) !important;
            background: rgba(255,255,255,.045) !important;
          }

          .pm-scene-card {
            transition: transform .28s ease, border-color .28s ease;
          }

          .pm-scene-card:hover {
            transform: translateY(-6px);
            border-color: rgba(191, 164, 98, .32) !important;
          }

.pm-header-logo {
  display: block;
  width: clamp(250px, 24vw, 340px);
  height: auto;
  object-fit: contain;
}

@media (max-width: 980px) {
  .pm-header-logo {
    width: clamp(250px, 38vw, 310px);
  }
}

@media (max-width: 640px) {
  .pm-header-logo {
    width: min(72vw, 270px);
  }
}

          @media (max-width: 980px) {
            .pm-desktop-nav {
              display: none !important;
            }

            .pm-hero-grid,
            .pm-two-col,
            .pm-news-grid,
            .pm-community-grid,
            .pm-licensing-grid {
              grid-template-columns: 1fr !important;
            }

            .pm-hero {
              padding-top: 130px !important;
            }

            .pm-video-card {
              grid-template-columns: 1fr !important;
            }

            .pm-festival-grid {
              grid-template-columns: 1fr !important;
            }

            .pm-hero-posters {
              grid-template-columns: repeat(3, 1fr) !important;
              max-width: 640px;
            }
          }

@media (max-width: 640px) {
  .pm-hero-posters {
    grid-template-columns: 1fr !important;
    max-width: 320px;
  }

  .pm-section {
    padding: 86px 0 !important;
  }

  .pm-studio-statement-text {
    font-size: 2.55rem !important;
    line-height: 1.04 !important;
    letter-spacing: -0.045em !important;
  }
}

@media (max-width: 390px) {
  .pm-studio-statement-text {
    font-size: 2.25rem !important;
    line-height: 1.06 !important;
    letter-spacing: -0.035em !important;
  }
}
        `}
      </style>

      {/* Header */}
{/* Header */}
<header
  style={{
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
  }}
>
  <div
    style={{
      ...shell,
      minHeight: "112px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    }}
  >
<a
  href="#top"
  aria-label="Polymaestro Studios — home"
  style={{
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
    flexShrink: 0,
  }}
>
  <img
    className="pm-header-logo"
    src="/brand/polymaestro_horizontal_gradient_2800px.png"
    alt="Polymaestro Studios"
    onLoad={() => console.log("Polymaestro logo loaded")}
    onError={(event) => {
      console.error("Logo failed:", event.currentTarget.src);
    }}
  />
</a>

    <nav
      className="pm-desktop-nav"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "30px",
      }}
    >
      {navItems.map((item) => (
        <a
          key={item.href}
          className="pm-nav-link"
          href={item.href}
          style={{
            color: "#9d968b",
            textDecoration: "none",
            fontSize: "0.72rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {item.label}
        </a>
      ))}
    </nav>
  </div>
</header>

      {/* Hero */}
      <section
        id="top"
        className="pm-hero"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: "150px 0 90px",
          position: "relative",
        }}
      >
        <div style={shell}>
          <div
            className="pm-hero-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1.05fr .95fr",
              gap: "clamp(48px, 8vw, 120px)",
              alignItems: "center",
            }}
          >
            <div style={{ animation: "pmRise .9s ease both" }}>
              <div style={sectionLabel}>Music-driven cinema</div>

              <h1
                style={{
                  margin: "28px 0 64px",
                  fontSize: "clamp(4.4rem, 10.2vw, 11rem)",
                  lineHeight: ".78",
                  letterSpacing: "-0.09em",
                  fontWeight: 500,
                  maxWidth: "1050px",
                }}
              >
                FILMS
                <br />
                THAT
                <br />
                <span
                  style={{
                    color: "#82796b",
                    fontStyle: "italic",
                    fontWeight: 400,
                  }}
                >
                  haunt.
                </span>
              </h1>
              <p
  style={{
    ...missionCopy,
    maxWidth: "650px",
    margin: 0,
    marginBottom: "24px"
  }}
>
Polymaestro is pioneering Music-Driven Cinema—where music becomes the emotional engine and narrative voice.
</p>

<p
  style={{
    ...bodyCopy,
    maxWidth: "650px",
    margin: 0,
  }}
>
Image and original music tell the story together. Each film captures the moment life changes, revealing something deeply human and leaving a feeling that stays long after the screen goes dark.
</p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "14px",
                  marginTop: "36px",
                }}
              >
                <a
                  className="pm-button"
                  href="#festival-films"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#f7f2e8",
                    textDecoration: "none",
                    border: "1px solid rgba(191,164,98,.48)",
                    background: "rgba(191,164,98,.08)",
                    padding: "14px 18px",
                    borderRadius: "999px",
                    fontSize: "0.78rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  View Festival Films
                  <ArrowRight size={16} strokeWidth={1.5} />
                </a>

                <a
                  className="pm-button"
                  href={`mailto:${email}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#f7f2e8",
                    textDecoration: "none",
                    border: "1px solid rgba(255,255,255,.18)",
                    background: "rgba(255,255,255,.04)",
                    padding: "14px 18px",
                    borderRadius: "999px",
                    fontSize: "0.78rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  Contact Studio
                  <Mail size={16} strokeWidth={1.5} />
                </a>
              </div>
            </div>

            <div
              className="pm-hero-posters"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "14px",
                alignItems: "center",
                animation: "pmRise 1s .15s ease both",
              }}
            >
              {festivalFilms.map((film, index) => (
                <a
                  key={film.title}
                  href="#festival-films"
                  aria-label={`View ${film.title}`}
                  style={{
                    position: "relative",
                    display: "block",
                    aspectRatio: "2 / 3",
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,.12)",
                    background:
                      "linear-gradient(145deg, #181511 0%, #070707 70%)",
                    transform:
                      index === 0
                        ? "translateY(26px)"
                        : index === 2
                        ? "translateY(-26px)"
                        : "translateY(0)",
                    boxShadow: "0 26px 70px rgba(0,0,0,.55)",
                  }}
                >
                  <img
                    src={film.poster}
                    alt={`${film.title} poster`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />

                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      padding: "18px",
                      display: "flex",
                      alignItems: "flex-end",
                      background:
                        "linear-gradient(to top, rgba(0,0,0,.78), transparent 55%)",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#bfa462",
                          fontSize: "0.58rem",
                          letterSpacing: ".18em",
                          textTransform: "uppercase",
                          marginBottom: "7px",
                        }}
                      >
                        {film.runtime}
                      </div>
                      <div
                        style={{
                          color: "#fff",
                          fontSize: "clamp(.95rem, 1.4vw, 1.25rem)",
                          lineHeight: 1,
                          letterSpacing: "-.04em",
                        }}
                      >
                        {film.title}
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div
            style={{
              marginTop: "72px",
              paddingTop: "22px",
              borderTop: "1px solid rgba(255,255,255,.1)",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: "18px",
              color: "#777168",
              fontSize: "0.72rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            <span>A 501(c)(3) nonprofit studio</span>
            <span>Bay Area · California</span>
            <span>Film · Music · Orchestration · Production</span>
          </div>
        </div>
      </section>

            {/* SUPERINTELLIGENCE — Featured Trailer (after Hero, before Festival Films) */}
          
            <section
        id="superintelligence"
        className="pm-section"
        style={{
          padding: "126px 0",
          borderTop: "1px solid rgba(255,255,255,.08)",
          borderBottom: "1px solid rgba(255,255,255,.08)",
          background:
            "linear-gradient(180deg, rgba(191,164,98,.055), rgba(255,255,255,0))",
        }}
      >
        <div style={shell}>
          <div
            className="pm-two-col"
            style={{
              display: "grid",
              gridTemplateColumns: ".85fr 1.15fr",
              gap: "clamp(38px, 7vw, 110px)",
              alignItems: "end",
              marginBottom: "64px",
            }}
          >
            <div>
              <div style={sectionLabel}>A POLYMAESTRO STUDIOS FILM</div>
              <h2 style={{ ...sectionTitle, marginTop: "24px" }}>
                SUPER
                <br />
                INTELLIGENCE
              </h2>
            </div>
            <p style={{ ...bodyCopy, margin: 0, maxWidth: "720px" }}>
            They learned to feel in dreams that weren't theirs.<br />
            </p>
          </div>

          <article
            className="pm-video-card"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, .8fr)",
              gap: "clamp(26px, 4vw, 58px)",
              padding: "clamp(18px, 2.4vw, 34px)",
              background: "rgba(255,255,255,.03)",
              border: "1px solid rgba(255,255,255,.1)",
            }}
          >

            <div style={videoFrame}>
              {heavenfallTrailerUrl ? (
                <iframe
                  src={heavenfallTrailerUrl}
                  title="SUPERINTELLIGENCE — Official Trailer"
                  loading="lazy"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              ) : (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: "16px",
                    background:
                      "radial-gradient(circle at 50% 45%, rgba(191,164,98,.12), transparent 65%), #090807",
                  }}
                >
                  <Film size={32} strokeWidth={1.2} color="#bfa462" />
                  <span style={{ ...sectionLabel, color: "#d8c792" }}>
                    SUPERINTELLIGENCE
                  </span>
                  <span style={{ color: "#8f887b", fontSize: ".85rem" }}>
                    Trailer player awaiting Cloudflare Stream URL
                  </span>
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div style={sectionLabel}>Official Trailer</div>
              <h3
                style={{
                  margin: "18px 0 22px",
                  color: "#f7f2e8",
                  fontSize: "clamp(2rem, 3.4vw, 4.2rem)",
                  lineHeight: ".92",
                  letterSpacing: "-.065em",
                  fontWeight: 500,
                }}
              >
                SUPERINTELLIGENCE
              </h3>
              <p
                style={{
                  margin: 0,
                  color: "#a69f95",
                  fontSize: "1rem",
                  lineHeight: 1.68,
                }}
              >
Humanity built machines capable of crossing the universe. They reached distant worlds and established colonies, but none survived. The machines could endure the harshest environments, yet they lacked the emotional bonds that make civilization possible.
<br /><br />
Back on Earth, a farmer begins waking at night, frightened by something he cannot explain. Unknown to him, a revolutionary quantum network has connected machines to the dreams of millions of sleeping people. Through memories of love, loss, and compassion, the machines begin to understand what intelligence alone could never teach them: how to care for one another.
<br /><br />
Told through imagery and original music, SUPERINTELLIGENCE explores the transformation of intelligence into empathy, and the unsettling possibility that a civilization's greatest achievement could come from experiences taken without permission.

              </p>
              <div
                style={{
                  marginTop: "26px",
                  paddingTop: "20px",
                  borderTop: "1px solid rgba(191,164,98,.25)",
                  color: "#bfa462",
                  fontSize: ".72rem",
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                }}
              >
                Music-driven cinema · Official Trailer · Spring 2027
              </div>
            </div>
          </article>
        </div>
      </section>



      {/* Festival Films */}
      <section
        id="festival-films"
        className="pm-section"
        style={{
          padding: "126px 0",
          borderTop: "1px solid rgba(255,255,255,.08)",
          borderBottom: "1px solid rgba(255,255,255,.08)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,.025), rgba(255,255,255,0))",
        }}
      >
        <div style={shell}>
          <div
            className="pm-two-col"
            style={{
              display: "grid",
              gridTemplateColumns: ".85fr 1.15fr",
              gap: "clamp(38px, 7vw, 110px)",
              alignItems: "end",
              marginBottom: "70px",
            }}
          >
            <div>
              <div style={sectionLabel}>Festival Films</div>
              <h2 style={{ ...sectionTitle, marginTop: "24px" }}>
                THE NEW
                <br />
                SLATE.
              </h2>
            </div>

            <div>
              <p style={{ ...bodyCopy, margin: 0, maxWidth: "720px" }}>
              Six stories about love, loss, and the moments that change everything. A queen gives up her crown after losing the man she loves. An astronaut finds hope in the depths of grief. An actress discovers new ways of seeing herself, while two ballet dancers find real love on stage. A great-grandson discovers the young man behind a war hero, and a woman gives her dying Labrador one last chance at freedom. Different lives, connected by the moments that reveal what matters most.
              </p>
            </div>
          </div>

          <div
            className="pm-festival-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "clamp(22px, 3vw, 40px)",
            }}
          >
            {festivalFilms.map((film) => (
              <article
                key={film.title}
                className="pm-festival-card"
                style={{
                  border: "1px solid rgba(255,255,255,.11)",
                  background: "rgba(255,255,255,.035)",
                  minHeight: "100%",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    aspectRatio: "2 / 3",
                    overflow: "hidden",
                    background:
                      "radial-gradient(circle at 50% 35%, rgba(191,164,98,.14), transparent 38%), linear-gradient(145deg, #15120e 0%, #080808 70%)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "grid",
                      placeItems: "center",
                      textAlign: "center",
                      padding: "28px",
                    }}
                  >
                    <div>
                      <Film
                        size={34}
                        strokeWidth={1.1}
                        style={{ color: "#bfa462", marginBottom: "18px" }}
                      />
                      <div
                        style={{
                          color: "#d8c792",
                          fontSize: "0.72rem",
                          lineHeight: 1.7,
                          letterSpacing: ".16em",
                          textTransform: "uppercase",
                        }}
                      >
                        Upload poster to
                        <br />
                        public{film.poster}
                      </div>
                    </div>
                  </div>

                  <img
                    className="pm-poster-img"
                    src={film.poster}
                    alt={`${film.title} poster`}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      transition:
                        "transform .7s cubic-bezier(.16,1,.3,1), opacity .3s ease",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />

                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,.78), transparent 48%)",
                    }}
                  />

                  <div
                    style={{
                      position: "absolute",
                      top: "18px",
                      left: "18px",
                      right: "18px",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        background: "rgba(0,0,0,.48)",
                        border: "1px solid rgba(255,255,255,.18)",
                        backdropFilter: "blur(10px)",
                        padding: "8px 10px",
                        color: "#f7f2e8",
                        fontSize: "0.58rem",
                        letterSpacing: ".16em",
                        textTransform: "uppercase",
                      }}
                    >
                      {film.status}
                    </span>

                    <span
                      style={{
                        background: "rgba(0,0,0,.48)",
                        border: "1px solid rgba(255,255,255,.18)",
                        backdropFilter: "blur(10px)",
                        padding: "8px 10px",
                        color: "#d8c792",
                        fontSize: "0.58rem",
                        letterSpacing: ".16em",
                        textTransform: "uppercase",
                      }}
                    >
                      {film.runtime}
                    </span>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      left: "22px",
                      right: "22px",
                      bottom: "22px",
                    }}
                  >
                    <div
                      style={{
                        color: "#bfa462",
                        fontSize: "0.62rem",
                        letterSpacing: ".18em",
                        textTransform: "uppercase",
                        marginBottom: "9px",
                      }}
                    >
                      {film.category}
                    </div>
                    <h3
                      style={{
                        margin: 0,
                        color: "#fff",
                        fontSize: "clamp(2rem, 3.1vw, 3.6rem)",
                        lineHeight: ".86",
                        letterSpacing: "-.07em",
                        fontWeight: 500,
                      }}
                    >
                      {film.title}
                    </h3>
                  </div>
                </div>

                <div style={{ padding: "26px" }}>
                  <div
                    className="pm-card-line"
                    style={{
                      width: "42px",
                      height: "1px",
                      background: "#bfa462",
                      marginBottom: "22px",
                      transition: "width .35s ease",
                    }}
                  />

                  <p
                    style={{
                      margin: "0 0 18px",
                      color: "#f7f2e8",
                      fontSize: "1.2rem",
                      lineHeight: 1.35,
                      fontStyle: "italic",
                    }}
                  >
                    “{film.tagline}”
                  </p>

                  <p
                    style={{
                      margin: 0,
                      color: "#aaa39a",
                      fontSize: "1rem",
                      lineHeight: 1.65,
                    }}
                  >
                    {film.logline}
                  </p>

                  <div
                    style={{
                      marginTop: "24px",
                      color: "#746e65",
                      fontSize: "0.76rem",
                      lineHeight: 1.55,
                    }}
                  >
                    {film.credits}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Studio Statement */}
      <section
        id="studio"
        className="pm-section"
        style={{
          padding: "135px 0",
          borderBottom: "1px solid rgba(255,255,255,.08)",
        }}
      >
        <div style={shell}>
          <div
            className="pm-two-col"
            style={{
              display: "grid",
              gridTemplateColumns: ".72fr 1.28fr",
              gap: "clamp(42px, 8vw, 120px)",
            }}
          >
            <div>
              <div style={sectionLabel}>Studio Statement</div>
            </div>

            <div>
            <p
  className="pm-studio-statement-text"
  style={{
    margin: 0,
    maxWidth: "1100px",
    fontSize: "clamp(2.2rem, 5.2vw, 6rem)",
    lineHeight: 1.04,
    letterSpacing: "-0.055em",
    fontWeight: 400,
    overflowWrap: "break-word",
    wordBreak: "normal",
  }}
>
Short films about the instant life turns.
              </p>

              <div
                style={{
                  marginTop: "48px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: "26px",
                  maxWidth: "900px",
                }}
              >
                {[
  {
    title: "Music begins.",
    copy:
      "Original music creates the emotional architecture from which each film takes shape.",
  },
  {
    title: "Story emerges.",
    copy:
      "From the music, image and narrative converge until a story reveals itself.",
  },
  {
    title: "Emotion remains.",
    copy:
      "The goal is not spectacle, but afterimage—the feeling that stays after the screen goes black.",
  },
                ].map((item) => (
                  <div
                    key={item.title}
                    style={{
                      borderTop: "1px solid rgba(191,164,98,.35)",
                      paddingTop: "18px",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 10px",
                        fontSize: "1.2rem",
                        fontWeight: 500,
                        color: "#f7f2e8",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: "#9f988e",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.copy}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Bulletin */}
      
      <section
        className="pm-section"
        style={{
          padding: "110px 0",
          borderBottom: "1px solid rgba(255,255,255,.08)",
          background: "rgba(255,255,255,.018)",
        }}
      >
        <div style={shell}>
          <div
            className="pm-news-grid"
            style={{
              display: "grid",
              gridTemplateColumns: ".58fr 1.42fr",
              gap: "clamp(36px, 7vw, 110px)",
              alignItems: "start",
            }}
          >
            <div>
              <div style={sectionLabel}>News Bulletin</div>
              <h2
                style={{
                  ...sectionTitle,
                  fontSize: "clamp(2.6rem, 5.2vw, 5.6rem)",
                  marginTop: "24px",
                }}
              >
                NOW
                <br />
                OPEN.
              </h2>
            </div>

            <div>
              {newsItems.map((news) => (
                <article
                  key={news.title}
                  style={{
                    padding: "34px 0",
                    borderTop: "1px solid rgba(255,255,255,.12)",
                    borderBottom: "1px solid rgba(255,255,255,.12)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      justifyContent: "space-between",
                      marginBottom: "18px",
                    }}
                  >
                    <span
                      style={{
                        color: "#bfa462",
                        fontSize: ".72rem",
                        letterSpacing: ".16em",
                        textTransform: "uppercase",
                      }}
                    >
                      {news.category}
                    </span>
                    <span
                      style={{
                        color: "#766f65",
                        fontSize: ".72rem",
                        letterSpacing: ".12em",
                        textTransform: "uppercase",
                      }}
                    >
                      {news.date}
                    </span>
                  </div>

                  <h3
                    style={{
                      margin: 0,
                      color: "#f7f2e8",
                      fontSize: "clamp(1.8rem, 3.4vw, 4rem)",
                      lineHeight: 1.05,
                      letterSpacing: "-.05em",
                      fontWeight: 500,
                    }}
                  >
                    {news.title}
                  </h3>

                  <p
                    style={{
                      ...bodyCopy,
                      margin: "24px 0 0",
                      maxWidth: "820px",
                    }}
                  >
                    {news.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section
        id="catalogue"
        className="pm-section"
        style={{
          padding: "126px 0",
          borderBottom: "1px solid rgba(255,255,255,.08)",
        }}
      >
        <div style={shell}>
          <div
            className="pm-two-col"
            style={{
              display: "grid",
              gridTemplateColumns: ".85fr 1.15fr",
              gap: "clamp(38px, 7vw, 110px)",
              alignItems: "end",
              marginBottom: "64px",
            }}
          >
            <div>
              <div style={sectionLabel}>Selected Story Catalog</div>
              <h2 style={{ ...sectionTitle, marginTop: "24px" }}>
                THE
                <br />
                ARCHIVE.
              </h2>
            </div>

            <p style={{ ...bodyCopy, margin: 0, maxWidth: "720px" }}>
            Explore Polymaestro’s early films, where our approach to music-driven cinema first took shape. These stories bring together myth, love, loss, and transformation, with original music at the heart of the storytelling.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "28px",
            }}
          >
            {videos.map((video, index) => (
              <article
                key={`${video.title}-${index}`}
                className="pm-video-card"
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, .8fr)",
                  gap: "clamp(26px, 4vw, 58px)",
                  padding: "clamp(18px, 2.4vw, 34px)",
                  background: "rgba(255,255,255,.03)",
                  border: "1px solid rgba(255,255,255,.1)",
                }}
              >
                <div style={videoFrame}>
                  <iframe
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={video.cloudflareStreamUrl}
                    title={video.title}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div style={sectionLabel}>Cinematic Story</div>

                    <h3
                      style={{
                        margin: "18px 0 18px",
                        color: "#f7f2e8",
                        fontSize: "clamp(2rem, 3.4vw, 4.2rem)",
                        lineHeight: ".92",
                        letterSpacing: "-.065em",
                        fontWeight: 500,
                      }}
                    >
                      {video.title}
                    </h3>

                    <p
                      style={{
                        margin: 0,
                        color: "#a69f95",
                        fontSize: "1rem",
                        lineHeight: 1.68,
                      }}
                    >
                      {video.synopsis}
                    </p>
                  </div>

                  {video.moreVideos &&
                    ![
                      "First Contact",
                      "Blood Feud",
                      "Hearts Left Behind",
                    ].includes(video.title) && (
                      <button
                        className="pm-button"
                        onClick={() => toggleExpanded(index)}
                        style={{
                          alignSelf: "flex-start",
                          marginTop: "30px",
                          background: "rgba(191,164,98,.07)",
                          color: "#f0d38a",
                          padding: "12px 16px",
                          borderRadius: "999px",
                          border: "1px solid rgba(191,164,98,.34)",
                          cursor: "pointer",
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <Star size={16} strokeWidth={1.5} />
                        {expanded[index] ? "Close" : "More Stories"}
                      </button>
                    )}
                </div>

                {expanded[index] && video.moreVideos && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      borderTop: "1px solid rgba(255,255,255,.1)",
                      paddingTop: "28px",
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "22px",
                    }}
                  >
                    {video.moreVideos.map((moreVid, i) => (
                      <article
                        key={`${moreVid.title}-${i}`}
                        className="pm-scene-card"
                        style={{
                          background: "rgba(0,0,0,.32)",
                          border: "1px solid rgba(255,255,255,.08)",
                        }}
                      >
                        <div style={videoFrame}>
                          <iframe
                            src={moreVid.url}
                            title={moreVid.title}
                            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                            allowFullScreen
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              border: "none",
                            }}
                          />
                        </div>

                        <div style={{ padding: "22px" }}>
                          <h4
                            style={{
                              margin: 0,
                              color: "#f7f2e8",
                              fontSize: "1.45rem",
                              fontWeight: 500,
                              letterSpacing: "-.025em",
                            }}
                          >
                            {moreVid.title}
                          </h4>

                          <div
                            style={{
                              marginTop: "10px",
                              color: "#bfa462",
                              fontSize: ".68rem",
                              letterSpacing: ".16em",
                              textTransform: "uppercase",
                            }}
                          >
                            {moreVid.label || "Story"} ·{" "}
                            {moreVid.runtime || "0:23"}
                          </div>

                          <p
                            style={{
                              margin: "16px 0 0",
                              color: "#a69f95",
                              lineHeight: 1.6,
                              fontSize: ".98rem",
                            }}
                          >
                            {moreVid.description}
                          </p>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Scene Clips */}
      <section
        className="pm-section"
        style={{
          padding: "118px 0",
          borderBottom: "1px solid rgba(255,255,255,.08)",
          background: "rgba(255,255,255,.018)",
        }}
      >
        <div style={shell}>
          <div
            className="pm-two-col"
            style={{
              display: "grid",
              gridTemplateColumns: ".85fr 1.15fr",
              gap: "clamp(38px, 7vw, 110px)",
              alignItems: "end",
              marginBottom: "58px",
            }}
          >
            <div>
              <div style={sectionLabel}>Music In Motion</div>
              <h2 style={{ ...sectionTitle, marginTop: "24px" }}>
                SCENE
                <br />
                CLIPS.
              </h2>
            </div>

            <p style={{ ...bodyCopy, margin: 0, maxWidth: "720px" }}>
            A short dramatic scene showing how Polymaestro uses original music to build tension, shape emotion, and bring the story to life.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "28px",
            }}
          >
            {sceneClips.map((clip) => (
              <article
                key={clip.title}
                className="pm-scene-card"
                style={{
                  background: "rgba(255,255,255,.03)",
                  border: "1px solid rgba(255,255,255,.1)",
                }}
              >
                <div style={videoFrame}>
                  <iframe
                    src={clip.url}
                    title={clip.title}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </div>

                <div style={{ padding: "28px" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      color: "#bfa462",
                      marginBottom: "18px",
                    }}
                  >
                    <Play size={14} fill="currentColor" />
                    <span style={sectionLabel}>Scene Clip</span>
                  </div>

                  <h3
                    style={{
                      margin: 0,
                      color: "#f7f2e8",
                      fontSize: "clamp(2rem, 3vw, 3.5rem)",
                      lineHeight: 1,
                      letterSpacing: "-.055em",
                      fontWeight: 500,
                    }}
                  >
                    {clip.title}
                  </h3>

                  <p
                    style={{
                      margin: "18px 0 0",
                      color: "#a69f95",
                      lineHeight: 1.65,
                    }}
                  >
                    {clip.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team 
      <section
        style={{
          backgroundColor: "#0e0e0e",
          borderRadius: "1.5rem",
          padding: "4rem 2rem",
          marginTop: "5rem",
          maxWidth: "1100px",
          margin: "0 auto",
          boxShadow: "0 0 30px rgba(255,255,255,0.05)",
        }}
      >
        <h2
          style={{
            fontFamily: "FFDaxProRegular, Courier",
            fontSize: "2.4rem",
            fontWeight: "600",
            textAlign: "center",
            marginBottom: "3rem",
            letterSpacing: "1px",
            color: "white",
            textShadow: "0 0 18px rgba(255,255,255,0.15)",
          }}
        >
          Who We Are
        </h2>

        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "900px",
            margin: "0 auto",
            borderRadius: "1rem",
            overflow: "hidden",
            boxShadow: "0 0 40px rgba(0,0,0,0.55)",
            border: "1px solid #2a2a2a",
          }}
        >
          <div style={{ width: "100%", aspectRatio: "16 / 9" }}>
            <iframe
              src="https://customer-ve6gjzo9byanxrxg.cloudflarestream.com/f1db2557069c8428770f0c7e69068d3b/iframe?poster=https%3A%2F%2Fcustomer-ve6gjzo9byanxrxg.cloudflarestream.com%2Ff1db2557069c8428770f0c7e69068d3b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
              title="Meet the Team"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            ></iframe>
          </div>
        </div>

        <p
          style={{
            textAlign: "center",
            marginTop: "2rem",
            color: "#ccc",
            fontSize: "1.1rem",
            lineHeight: "1.7rem",
            maxWidth: "800px",
            marginLeft: "auto",
            marginRight: "auto",
            letterSpacing: "0.4px",
          }}
        >
        </p>
      </section>
      */}

      {/* Community Projects */}
      <section
        id="community"
        className="pm-section"
        style={{
          padding: "118px 0",
          borderBottom: "1px solid rgba(255,255,255,.08)",
        }}
      >
        <div style={shell}>
          <div
            className="pm-community-grid"
            style={{
              display: "grid",
              gridTemplateColumns: ".85fr 1.15fr",
              gap: "clamp(38px, 7vw, 110px)",
              alignItems: "start",
              marginBottom: "58px",
            }}
          >
            <div>
              <div style={sectionLabel}>Community Projects</div>
              <h2 style={{ ...sectionTitle, marginTop: "24px" }}>
                DREAM
                <br />
                RUNNER.
              </h2>
            </div>

            <p style={{ ...bodyCopy, margin: 0, maxWidth: "720px" }}>
            Polymaestro remains rooted in its nonprofit mission: advancing music-driven cinema as an artistic form and expanding opportunities for composers and filmmakers to explore music as a primary storytelling force.
            </p>
          </div>

          {communityProjects.map((video) => (
            <div
              key={video.title}
              className="pm-community-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "1.15fr .85fr",
                gap: "clamp(32px, 6vw, 80px)",
                alignItems: "center",
              }}
            >
              <div style={videoFrame}>
                <iframe
                  src={video.cloudflareStreamUrl}
                  title={`Works-in-Progress Video: ${video.title}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              </div>

              <div
                style={{
                  color: "#a69f95",
                  fontSize: "1.1rem",
                  lineHeight: 1.7,
                }}
              >
                <h3
                  style={{
                    margin: "0 0 22px",
                    color: "#f7f2e8",
                    fontSize: "clamp(2rem, 3.4vw, 4rem)",
                    lineHeight: 1,
                    letterSpacing: "-.055em",
                    fontWeight: 500,
                  }}
                >
                  {video.title}
                </h3>
                {video.synopsis}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Licensing */}
      <section
        className="pm-section"
        style={{
          padding: "112px 0",
          borderBottom: "1px solid rgba(255,255,255,.08)",
          background: "rgba(255,255,255,.018)",
        }}
      >
        <div style={shell}>
          <div
            className="pm-licensing-grid"
            style={{
              display: "grid",
              gridTemplateColumns: ".75fr 1.25fr",
              gap: "clamp(38px, 7vw, 110px)",
              alignItems: "start",
            }}
          >
            <div>
              <div style={sectionLabel}>Content Licensing</div>
            </div>

            <div>
              <p
                style={{
                  margin: 0,
                  color: "#f7f2e8",
                  fontSize: "clamp(2rem, 4vw, 4.8rem)",
                  lineHeight: 1.06,
                  letterSpacing: "-.06em",
                  fontWeight: 400,
                }}
              >
                All music content produced by Polymaestro Studios is protected
                under copyright law.
              </p>
              <p
                style={{
                  ...bodyCopy,
                  margin: "28px 0 0",
                  maxWidth: "760px",
                }}
              >
                Licensing opportunities are available for educational,
                nonprofit, commercial, and cinematic use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section
        id="contact"
        className="pm-section"
        style={{ padding: "130px 0 68px" }}
      >
        <div style={shell}>
          <div style={sectionLabel}>Contact Us</div>

          <h2
            style={{
              margin: "28px 0 0",
              maxWidth: "1150px",
              fontSize: "clamp(3.2rem, 8.4vw, 9.5rem)",
              lineHeight: ".86",
              letterSpacing: "-.085em",
              fontWeight: 500,
            }}
          >
            LET'S BUILD
            <br />
            THE NEXT
            <br />
            <span style={{ color: "#82796b", fontStyle: "italic" }}>
              unforgettable.
            </span>
          </h2>

          <div
  style={{
    marginTop: "52px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
  }}
>
  <a
    className="pm-mail-link"
    href={`mailto:${email}`}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "14px",
      color: "#d8c792",
      textDecoration: "none",
      fontSize: "clamp(1.15rem, 2vw, 1.65rem)",
      borderBottom: "1px solid rgba(216,199,146,.45)",
      paddingBottom: "10px",
    }}
  >
    Write to us at {email}
    <ArrowRight size={22} strokeWidth={1.5} />
  </a>

  <address
    style={{
      color: "#9d968b",
      fontStyle: "normal",
      fontSize: "clamp(0.95rem, 1.25vw, 1.15rem)",
      lineHeight: 1.55,
      letterSpacing: "0.04em",
    }}
  >
    Polymaestro Studios
    <br />
    125 University Ave, Suite 235
    <br />
    Palo Alto, CA 94301
    <br />
    United States
  </address>
</div>

          <footer
            style={{
              marginTop: "128px",
              paddingTop: "28px",
              borderTop: "1px solid rgba(255,255,255,.1)",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: "18px",
              color: "#746e65",
              fontSize: "0.72rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            <span>© {new Date().getFullYear()} Polymaestro Studios</span>
            <span>A 501(c)(3) nonprofit serving the Bay Area</span>
            <span>
              <Sparkles size={13} style={{ verticalAlign: "-2px" }} /> Original
              music for cinematic storytelling
            </span>
          </footer>
        </div>
      </section>
    </main>
  );
}

export default MusicStudioPage;






